import dayjs from "dayjs";
import { isHtmlEmpty } from "@/utils/format";
import type { FormPage } from "../types";
import type { BaseField, FormattedForm } from "./types";
import { buildFormattedField, buildValidFieldRefs } from "./helpers/formatter";
import { buildDeformField } from "./helpers/deformatter";
import { applyDynamicFieldConfig } from "./helpers/dynamic";

export { defaultValueConverter } from "./helpers/formatter";
export type {
  FormattedForm,
  Conditions,
  Step,
  Section,
  BaseField,
  Config,
  FieldCondition,
} from "./types";

// ----------------------------------------------------------------------
// ---------------------- Formatted form --------------------------------
// ----------------------------------------------------------------------

export const formattedForm = (
  title: string,
  form: FormPage[],
  formId?: string,
  target_user_type?: string,
): FormattedForm => {
  const validFieldRefs = buildValidFieldRefs(form);

  return {
    name: title,
    description: "Descripción de formulario",
    status: "published",
    id: formId || null,
    ...(target_user_type ? { target_user_type } : {}),
    steps: form.map((page, pageIndex) => ({
      name: page.name,
      id: page.id?.includes("page") ? null : (page.id ?? null),
      description: page.sections.map((s) => s.description).join(" "),
      order: pageIndex + 1,
      sections: page.sections.map((section, sectionIndex) => ({
        title: section.name,
        id: section.id.includes("section") ? null : section.id,
        description: isHtmlEmpty(section.description)
          ? ""
          : section.description,
        order: sectionIndex + 1,
        fields: section.fields.map((field, fieldIndex) =>
          buildFormattedField(field, fieldIndex, form, validFieldRefs),
        ),
      })),
    })),
  };
};

// -------------------------------------------------------------
// ---------------- reconstruir formulario ---------------------
// -------------------------------------------------------------

export const deformatForm = (form: FormattedForm): FormPage[] => {
  const nameToLabel: Record<string, string> = {};
  for (const step of form.steps ?? []) {
    for (const section of step.sections ?? []) {
      for (const f of section.fields ?? []) {
        if (f?.name) {
          nameToLabel[f.name] = f.label ?? f.name;
        }
      }
    }
  }

  return form.steps.map((step, stepIndex) => ({
    id: step.id || `page_${stepIndex + 1}`,
    name: step.name,
    sections: step.sections.map((section, sectionIndex) => ({
      id: section.id || `section_${stepIndex + 1}_${sectionIndex + 1}`,
      name: section.title,
      technicalKey: section.title.toLowerCase().replace(/\s+/g, "_"),
      description: section.description,
      fields: section.fields.map((baseField, fieldIndex) =>
        buildDeformField(baseField, fieldIndex, nameToLabel),
      ),
    })),
  }));
};

/**
 * Applies dynamic date constraints to form steps at render time.
 * When a date config value is "now", it is replaced with today's date.
 *
 * This ensures that date constraints like "no earlier than today" remain dynamic
 * instead of being hardcoded to a specific date at configuration time.
 */
export function applyDynamicDates<
  S extends { sections?: { fields?: unknown[] }[] },
>(steps: S[]): S[] {
  const today = dayjs().format("DD/MM/YYYY");
  const todayWithTime = dayjs().format("DD/MM/YYYY HH:mm");
  const currentYear = new Date().getFullYear();

  return steps.map((step) => ({
    ...step,

    sections: step.sections?.map((section) => ({
      ...section,

      fields: section.fields?.map((field) =>
        applyDynamicFieldConfig(
          field as BaseField,
          today,
          todayWithTime,
          currentYear,
        ),
      ),
    })),
  })) as S[];
}
